<template>
	<aside
		class="c-siteLogin"
		:class="{ 'c-siteLogin--open': isIndex }"
		aria-label="Site login"
	>
		<div class="c-siteLogin__button" @click="open" :class="btnClasses">
			<ProfileIcon v-if="button == 'account'"></ProfileIcon>
			<span class="text" v-if="button == 'text'">{{
				translations["Log in"]
			}}</span>
		</div>

		<SlideInOverlay
			:is-open="isOpen"
			placement="right"
			@close="close"
			@enter="enter"
			class="c-siteLogin__overlay c-siteLogin__overlay--login"
		>
			<template v-slot:header>
				<h2 v-if="version == 'login'">{{ translations["Login"] }}</h2>
				<h2 v-if="version == 'reset' || version == 'confirmation'">
					{{ translations["Reset password"] }}
				</h2>

				<h2 v-if="version == 'signup' || version == 'activation'">
					{{ translations["createAccount"] }}
				</h2>

				<button
					:aria-label="translations['Close']"
					class="close"
					@click="close"
				>
					<CloseIcon></CloseIcon>
				</button>
			</template>
			<template v-slot:body>
				<div class="error" v-if="error">
					<span>{{ error }}</span>
				</div>

				<div v-if="version == 'confirmation'">
					<span>
						{{
							translations[
								"If an account with the given username exists, you will shortly receive a email with an reset link in it."
							]
						}}
					</span>
				</div>

				<div v-if="version == 'activation'">
					<span>
						{{ translations["activationInstructions"] }}
					</span>
				</div>

				<FieldInput
					:id="'firstName'"
					:placeholder="translations['First name']"
					:required="true"
					:schema="'text'"
					type="text"
					v-model="firstName"
					:disabled="loading || successAnimation"
					v-if="version == 'signup'"
				></FieldInput>

				<FieldInput
					:id="'lastName'"
					:placeholder="translations['Last name']"
					:required="true"
					:schema="'text'"
					type="text"
					v-model="lastName"
					:disabled="loading || successAnimation"
					v-if="version == 'signup'"
				></FieldInput>

				<FieldInput
					:id="'email'"
					:placeholder="translations['E-mail']"
					:required="true"
					:schema="'email'"
					type="email"
					v-model="email"
					:disabled="loading || successAnimation"
					v-if="version == 'login' || version == 'reset' || version == 'signup'"
				></FieldInput>

				<FieldInput
					:id="'password'"
					:placeholder="translations['Password']"
					:required="true"
					:schema="'password'"
					type="password"
					v-model="password"
					:disabled="loading || successAnimation"
					v-if="version == 'login' || version == 'signup'"
				></FieldInput>

				<div class="body-options">
					<FieldCheckbox
						:id="'rememberMe'"
						v-model="rememberMe"
						:disabled="loading || successAnimation"
						v-if="version == 'login'"
					>
						{{ translations["Remember me"] }}
					</FieldCheckbox>

					<div
						class="create-account"
						@click="version = 'signup'"
						:aria-label="translations['createAccount']"
						v-if="version == 'login'"
					>
						{{ translations["createAccount"] }}
					</div>
				</div>
			</template>
			<template v-slot:footer>
				<div class="functions" v-if="version == 'login'">
					<button
						class="o-btn o-btn--fill"
						:class="successAnimation ? 'o-btn--green' : ''"
						@click="login"
						:aria-label="translations['Login']"
					>
						<TransitionGroup name="fade">
							<Loader v-if="loading"></Loader>
							<div
								class="success-wrapper"
								v-else-if="!loading && successAnimation"
							>
								<AniAddedToCart></AniAddedToCart>
								{{ translations["Logged in"] }}
							</div>
							<span v-else>{{ translations["Login"] }}</span>
						</TransitionGroup>
					</button>
					<div
						class="secondary"
						:class="{ 'secondary--disabled': loading || successAnimation }"
					>
						<button
							class="reset"
							@click="version = 'reset'"
							:aria-label="translations['Reset']"
						>
							{{ translations["Forgot password?"] }}
						</button>
					</div>
				</div>
				<div class="functions" v-if="version == 'signup'">
					<button
						:aria-label="translations['createAccount']"
						class="o-btn o-btn--fill"
						@click="createAccount"
					>
						<Loader v-if="loading"></Loader>
						<span v-else>{{ translations["createAccount"] }}</span>
					</button>
					<div class="secondary">
						<button
							:aria-label="translations['Login']"
							@click="version = 'login'"
						>
							{{ translations["Login"] }}
						</button>
					</div>
				</div>
				<div class="functions" v-if="version == 'reset'">
					<button
						:aria-label="translations['Reset']"
						class="o-btn o-btn--fill"
						@click="reset"
					>
						<Loader v-if="loading"></Loader>
						<span v-else>{{ translations["Reset password"] }}</span>
					</button>
					<div class="secondary">
						<button
							:aria-label="translations['Login']"
							@click="version = 'login'"
						>
							{{ translations["Login"] }}
						</button>
					</div>
				</div>
				<div class="functions" v-if="version == 'confirmation'">
					<button
						:aria-label="translations['Login']"
						class="o-btn o-btn--fill"
						@click="version = 'login'"
					>
						{{ translations["Login"] }}
					</button>
				</div>
				<div class="functions" v-if="version == 'activation'">
					<button
						:aria-label="translations['Login']"
						class="o-btn o-btn--fill"
						@click="version = 'login'"
					>
						{{ translations["Login"] }}
					</button>
				</div>
			</template>
		</SlideInOverlay>
	</aside>
</template>

<script>
import SlideInOverlay from "@/vue/components/objects/SlideInOverlay.vue";
import * as map from "@/vue/mixins/map";
import request from "@/vue/mixins/request.js";

// ICONS
import CloseIcon from "@web/assets/icon/close.svg";
import ProfileIcon from "@web/assets/icon/profile.svg";
import FieldInput from "@/vue/components/fields/FieldInput";
import FieldCheckbox from "@/vue/components/fields/FieldCheckbox.vue";
import Loader from "./objects/Loader.vue";
import AniAddedToCart from "@/vue/components/animation/AddedToCart.vue";

export default {
	name: "SiteLogin",
	mixins: [map.site, request],
	components: {
		SlideInOverlay,
		CloseIcon,
		ProfileIcon,
		FieldInput,
		FieldCheckbox,
		Loader,
		AniAddedToCart,
	},
	props: {
		button: {
			type: String,
			default: "account",
		},

		btnClasses: {
			type: String,
			default: "",
		},
	},
	data: () => {
		return {
			isOpen: false,
			isIndex: false,
			version: "login",

			email: "",
			firstName: "",
			lastName: "",
			password: "",
			rememberMe: false,

			successAnimation: false,
			error: false,
		};
	},
	methods: {
		close() {
			this.isOpen = false;

			setTimeout(() => {
				this.isIndex = false;
			}, 250);
		},
		open() {
			this.isOpen = true;
			this.isIndex = true;
		},
		enter() {
			this.login();
		},
		login() {
			this.error = false;

			const formData = new FormData();
			formData.append("action", "quantity-account/users/login");

			// Fields
			formData.append("loginName", this.email);
			formData.append("password", this.password);

			// Checkbox
			if (this.rememberMe) {
				formData.append("rememberMe", 1);
			}

			// Send request to log user in.
			this.request(formData).then(() => {
				// If any errors occour, stop everything and show error
				if (this.res.data.errorCode) {
					this.error = this.res.data.message;
				}

				//If successful handle, success animations, user login and slidein overlays close animation.
				else if (this.res.data.success) {
					//Start successAnimation
					this.successAnimation = true;

					//After success animation is done, close the login overlay
					setTimeout(() => {
						//Calls the login overlays, slide out animation
						this.isOpen = false;

						//Call nested timeout to ensure the login overlays, slide out animation is not interrupted.
						setTimeout(() => {
							//the "commerce/cart" dispatch triggers the isLoggedIn global variable to be set to true.
							//When isLoggedIn is set to true, this component is no longer displayed.
							this.$store.dispatch("commerce/cart", this.res.data.cart);
							this.$store.commit(
								"app/setCsrfToken",
								this.res.data.csrfTokenValue
							);
							this.$emit("login", this.res.data);
							//Slide out animation is 0.4s therefor the timeout is set to 400ms
						}, 400);
						//Animation length 1.85s therefore the initial timeout is set to 1850ms
					}, 1850);
				}
			});
		},

		reset() {
			this.error = false;

			const formData = new FormData();
			// We use our own custom endpoint as we want to re-send activation emails to users who has not yet activated their account.
			formData.append("action", "quantity-app/user/send-password-reset-email")
			formData.append("loginName", this.email);

			this.request(formData).then(() => {
				this.version = "confirmation";
			});
		},
		createAccount() {
			this.error = false;

			if (!this.firstName || !this.lastName) {
				this.error = "Indtast venligst fornavn og efternavn";
				return;
			}

			const formData = new FormData();
			formData.append("action", "users/save-user");
			formData.append("email", this.email);
			formData.append("username", this.email);
			formData.append("fullName", this.firstName + " " + this.lastName);
			formData.append("password", this.password);

			this.request(formData).then(() => {
				if (!this.res.data.errors) {
					this.version = "activation";
				}

				if (this.res.data.errors.email) {
					this.error = this.res.data.errors.email[0];
					return;
				}

				if (this.res.data.errors.password) {
					this.error = this.res.data.errors.password[0];
					return;
				}

				this.error = this.res.data.message;
			});
		},
	},
};
</script>

<style lang="scss">
@import "@backend/scss/abstracts/variables.scss";
@import "@backend/scss/abstracts/mixins.scss";

.c-siteLogin {
	z-index: 9;
	height: 100%;

	&__button {
		height: 100%;
		display: flex;
		align-items: center;
		cursor: pointer;

		svg {
			width: 20px;
			height: 20px;
		}

		.text {
			@include hover-underline($reverse: true);
			font-weight: $f-semibold;
		}
	}

	.inner {
		position: relative;
		z-index: 2;
		flex-direction: column;

		&__header {
			width: 100%;
			padding-inline: 40px;
			padding-top: 40px;
			padding-bottom: 24px;

			display: flex;
			justify-content: space-between;

			h2 {
				line-height: 24px;
			}

			.close {
				cursor: pointer;
				width: 35px;
				height: 35px;

				display: flex;
				align-items: flex-start;
				justify-content: flex-end;

				svg {
					width: 16px;
					height: 16px;
				}
			}
			border-bottom: $e-border;
		}
		&__body {
			padding-top: 40px;
			padding-inline: 40px;

			.error {
				padding: 13px 16px;
				border: 1px solid $c-red;
				color: $c-red;

				display: flex;
				justify-content: center;
				margin-bottom: 14px;
				text-align: center;
			}

			.o-checkbox {
				&__wrapper {
					margin-top: 8px;
				}
			}
			.body-options {
				display: flex;
				justify-content: space-between;
			}
			.create-account {
				@include hover-underline($reverse: true);
				font-weight: 400;
				&::after {
					bottom: 4px;
				}
				&:hover {
					cursor: pointer;
					opacity: 0.7;
				}
			}
		}

		&__footer {
			.functions {
				border-top: $e-border;
				padding-top: 24px;
				padding-bottom: 24px;
				padding-inline: 40px;
				flex-direction: column;
				display: flex;

				.success-wrapper {
					display: flex;
					.ca-addedToCart {
						margin-right: 14px;
					}
				}
				.o-btn {
					&--green {
						background-color: $c-green;
					}
				}
			}
			.button {
				display: flex;
				justify-content: center;

				a {
					font-weight: $f-semibold;
					@include hover-underline($reverse: true);
				}
			}

			.secondary {
				transition: $t-hover;
				display: flex;
				justify-content: center;

				padding-top: 16px;
				padding-bottom: 2px;

				button {
					font-weight: $f-semibold;
					@include hover-underline($reverse: true);
				}

				&--disabled {
					transition: $t-hover;
					opacity: 0;
					pointer-events: none;
				}
			}
		}
	}

	&--open {
		z-index: 21;
	}
}

@media all and (max-width: 768px) {
	.c-siteLogin {
		.inner {
			&__header {
				padding-inline: 30px;
			}
			&__body {
				padding-inline: 30px;
			}
			&__footer {
				padding-inline: 30px;
			}
		}
	}
}

// @media all and (max-width: 768px) {
// 	.c-siteLogin {
// 		z-index: 21;
// 	}
// }

@media all and (max-width: 450px) {
	.c-siteLogin {
		.inner {
			&__header {
				padding-inline: $s-side;
			}
			&__body {
				padding-inline: $s-side;
			}
			&__footer {
				padding-inline: $s-side;
			}
		}
	}
}

@media all and (max-width: 360px) {
	.c-siteLogin {
		.inner {
			&__footer {
				.total {
					p {
						line-height: 18px;
						display: flex;
						flex-direction: column;

						span {
							font-size: $font-disclaimer-size;
						}
					}
				}
			}
		}
	}
}

@keyframes rippleScale {
	0% {
		transform: scale(1);
		opacity: 1;
	}
	100% {
		transform: scale(5);
		opacity: 0;
	}
}
</style>
